import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

/**
 * M20 L20.09 — Integration usage / API-call analytics.
 *
 * Reads `GET /v1/integration/monitoring/api-calls?since=` and renders the
 * aggregate counters plus a per-event breakdown.
 */
interface UsageSummary {
  readonly totalCalls: number;
  readonly authFailures: number;
  readonly rateLimitHits: number;
  readonly webhookTriggers: number;
  readonly eventsPublished: number;
  readonly syncRuns: number;
  readonly byEvent: { auditEvent: string; count: number }[];
}

const WINDOWS = [
  { key: 'm20.usage.window24h', hours: 24 },
  { key: 'm20.usage.window7d', hours: 24 * 7 },
  { key: 'm20.usage.window30d', hours: 24 * 30 },
];

function Stat({ label, value, testId }: { label: string; value: number; testId: string }): ReactNode {
  return (
    <div data-testid={testId} style={{ border: '1px solid var(--color-border-default)', borderRadius: 6, padding: 12, minWidth: 120 }}>
      <div style={{ fontSize: 22, fontWeight: 600 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{label}</div>
    </div>
  );
}

export function IntegrationUsagePage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [data, setData] = useState<UsageSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hours, setHours] = useState(24);

  useEffect(() => {
    let alive = true;
    setData(null);
    setError(null);
    const since = new Date(Date.now() - hours * 3600_000).toISOString();
    apiRequest<{ data?: UsageSummary } & Partial<UsageSummary>>(
      `/v1/integration/monitoring/api-calls?since=${encodeURIComponent(since)}`, { token },
    )
      .then((r) => { if (alive) setData((r.data ?? (r as UsageSummary))); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, hours]);

  return (
    <main aria-label="integration-usage">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>{t('m20.usage.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16, fontSize: 13 }}>{t('m20.usage.subtitle')}</p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
        <label htmlFor="window-filter" style={{ fontSize: 12 }}>{t('m20.usage.windowLabel')}</label>
        <select id="window-filter" aria-label="window-filter" value={hours} onChange={(e) => setHours(Number(e.target.value))}
                style={{ padding: '5px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
          {WINDOWS.map((w) => <option key={w.hours} value={w.hours}>{t(w.key)}</option>)}
        </select>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!data ? (
        !error && <p>{t('m20.usage.loading')}</p>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
            <Stat testId="stat-total" label={t('m20.usage.statTotal')} value={data.totalCalls} />
            <Stat testId="stat-auth-failures" label={t('m20.usage.statAuthFailures')} value={data.authFailures} />
            <Stat testId="stat-rate-limit" label={t('m20.usage.statRateLimit')} value={data.rateLimitHits} />
            <Stat testId="stat-webhooks" label={t('m20.usage.statWebhooks')} value={data.webhookTriggers} />
            <Stat testId="stat-events" label={t('m20.usage.statEvents')} value={data.eventsPublished} />
            <Stat testId="stat-sync" label={t('m20.usage.statSync')} value={data.syncRuns} />
          </div>

          <h2 style={{ fontSize: 15, marginBottom: 8 }}>{t('m20.usage.byEvent')}</h2>
          {data.byEvent.length === 0 ? (
            <p data-testid="byevent-empty" style={{ color: 'var(--color-text-muted)' }}>{t('m20.usage.noEvents')}</p>
          ) : (
            <table aria-label="byevent-table" style={{ width: '100%', maxWidth: 480, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border-default)', textAlign: 'left' }}>
                  <th style={{ padding: '8px 4px' }}>{t('m20.usage.colEvent')}</th>
                  <th style={{ padding: '8px 4px' }}>{t('m20.usage.colCount')}</th>
                </tr>
              </thead>
              <tbody>
                {data.byEvent.map((e) => (
                  <tr key={e.auditEvent} data-testid={`byevent-${e.auditEvent}`} style={{ borderBottom: '1px solid var(--color-border-default)' }}>
                    <td style={{ padding: '8px 4px' }}>{e.auditEvent}</td>
                    <td style={{ padding: '8px 4px' }}>{e.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
